import axios from 'axios';

const getApiBaseUrl = () => {
  if (typeof window !== 'undefined') {
    const host = window.location.host;
    if (!host.includes('localhost') && !host.includes('127.0.0.1')) {
      if (host.includes('turnohub.com.ar')) {
        return 'https://api.turnohub.com.ar';
      }
      return `${window.location.protocol}//${host}`;
    }
  }
  return process.env.REACT_APP_API_BASE_URL || '';
};

export const API_BASE_URL = getApiBaseUrl();

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'ngrok-skip-browser-warning': 'true'
  }
});

// Configurar axios globalmente para las llamadas directas que no usan apiClient
axios.defaults.headers.common['ngrok-skip-browser-warning'] = 'true';

// Interceptor para agregar token a las solicitudes
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para manejar errores
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && !/\/auth\/(login|register|reset-password|forgot-password)/.test(error.config?.url || '')) {
      // Token expirado o inválido
      localStorage.removeItem('token');
      window.location.href = '/login';
    } else if (error.response?.status === 403 && error.response?.data?.subscriptionExpired) {
      // Suscripción expirada
      if (typeof window !== 'undefined' && window.location.pathname !== '/subscription-expired') {
        window.location.href = '/subscription-expired';
      }
    } else if (error.response?.status >= 500) {
      // Error del servidor - Notificado a soporte
      console.error('⚠️ Error 500 detectado:', error.response?.data?.message || error.message);
    }
    return Promise.reject(error);
  }
);

// Servicios de Autenticación
export const authAPI = {
  verifySecondFactor: async (challengeToken, code) => (await axios.post(API_BASE_URL + '/api/auth/login/2fa', { challengeToken, code })).data,
  login: async (email, password) => {
    const response = await axios.post(API_BASE_URL + '/api/auth/login', {
      email,
      password
    });
    return response.data;
  },

  register: async (data) => {
    const response = await apiClient.post('/api/auth/register', data);
    return response.data;
  },

  verify: async (token) => {
    const response = await axios.get(API_BASE_URL + '/api/auth/verify', {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    return response.data;
  },

  logout: async (token) => {
    const response = await apiClient.post('/api/auth/logout', {}, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    return response.data;
  }
};

// Servicios de Doctor (próximamente)
export const doctorAPI = {
  getProfile: async () => {
    const response = await apiClient.get('/api/doctor/profile');
    return response.data;
  },

  updateProfile: async (data) => {
    const response = await apiClient.patch('/api/doctor/profile', data);
    return response.data;
  },

  getDashboard: async (date) => {
    const response = await apiClient.get(date ? `/api/doctor/dashboard?date=${date}` : '/api/doctor/dashboard');
    return response.data;
  },

  getWorkingHours: async () => {
    const response = await apiClient.get('/api/doctor/working-hours');
    return response.data;
  },

  updateWorkingHours: async (workingHours) => {
    const response = await apiClient.post('/api/doctor/working-hours', { availability: workingHours });
    return response.data;
  },

  getPushPublicKey: async () => {
    const response = await apiClient.get('/api/doctor/push-subscription/public-key');
    return response.data;
  },

  savePushSubscription: async (subscription) => {
    const response = await apiClient.post('/api/doctor/push-subscription', { subscription });
    return response.data;
  },

  deletePushSubscription: async (endpoint) => {
    const response = await apiClient.post('/api/doctor/push-subscription/unsubscribe', { endpoint });
    return response.data;
  },

  sendTestPushNotification: async () => {
    const response = await apiClient.post('/api/doctor/push-subscription/test');
    return response.data;
  }
};

// Servicios de Appointments (próximamente)
export const appointmentAPI = {
  getWorkingHours: async () => (await apiClient.get('/api/availability')).data,
  verifyPublicPayment: async token => (await axios.post(`${API_BASE_URL}/api/appointments/public/verify-payment/${token}`)).data,
  getAppointments: async () => {
    const response = await apiClient.get('/api/appointments');
    return response.data;
  },

  createAppointment: async (data) => {
    const response = await apiClient.post('/api/appointments', data);
    return response.data;
  },

  updateAppointment: async (appointmentId, data) => {
    const response = await apiClient.patch(`/api/appointments/${appointmentId}`, data);
    return response.data;
  },

  getAvailableSlots: async (date) => {
    const response = await apiClient.get(`/api/appointments/available-slots?date=${date}`);
    return response.data;
  },

  getByToken: async (token) => {
    // Esta llamada no requiere autenticación
    const response = await axios.get(`${API_BASE_URL}/api/appointments/public/${token}`);
    return response.data;
  },

  searchByPatientData: async (searchData) => {
    // Esta llamada no requiere autenticación - busca por datos del paciente
    const response = await axios.post(`${API_BASE_URL}/api/appointments/public/search`, searchData);
    return response.data;
  },

  getPublicRubros: async () => {
    // Obtener rubros disponibles (sin autenticación)
    const response = await axios.get(`${API_BASE_URL}/api/appointments/public/rubros`);
    return response.data;
  },

  getPublicSpecializations: async (rubro) => {
    // Obtener especialidades disponibles (sin autenticación)
    const url = rubro
      ? `${API_BASE_URL}/api/appointments/public/specializations?rubro=${encodeURIComponent(rubro)}`
      : `${API_BASE_URL}/api/appointments/public/specializations`;
    const response = await axios.get(url);
    return response.data;
  },

  getPublicDoctors: async (specialization) => {
    // Obtener médicos por especialidad (sin autenticación)
    const response = await axios.get(`${API_BASE_URL}/api/appointments/public/doctors/${encodeURIComponent(specialization)}`);
    return response.data;
  },

  getAllPublicDoctors: async () => {
    // Obtener todos los médicos aprobados (sin autenticación)
    const response = await axios.get(`${API_BASE_URL}/api/appointments/public/all-doctors`);
    return response.data;
  },

  getPublicAvailableSlots: async (doctorId, date, duration = 30) => {
    // Obtener horarios disponibles (sin autenticación)
    const response = await axios.get(`${API_BASE_URL}/api/appointments/public/available-slots/${doctorId}/${date}?duration=${duration}`);
    return response.data;
  },
  getPublicServices: async (doctorId) => {
    // Obtener servicios de un doctor (sin autenticación)
    const response = await axios.get(`${API_BASE_URL}/api/services/doctor/${doctorId}`);
    return response.data;
  },
  getDoctorAvailability: async (doctorId) => {
    // Obtener días de atención y vacaciones (sin autenticación)
    const response = await axios.get(`${API_BASE_URL}/api/appointments/public/doctor/${doctorId}/availability`);
    return response.data;
  },

  getPublicPatientDetails: async (doctorId, documentNumber, accessToken) => {
    const response = await axios.get(`${API_BASE_URL}/api/appointments/public/patient-details/${doctorId}/${encodeURIComponent(documentNumber)}`, { headers: { 'X-Patient-Access': accessToken } });
    return response.data;
  },

  updateDelay: async (appointmentId, data) => {
    const response = await apiClient.patch(`/api/appointments/${appointmentId}/delay`, data);
    return response.data;
  },

  createPublicAppointment: async (data) => {
    // Esta llamada no requiere autenticación - crea un turno desde el portal público
    const response = await axios.post(`${API_BASE_URL}/api/appointments/public/create`, data);
    return response.data;
  },

  cancelPublic: async (token) => {
    // Esta llamada no requiere autenticación - permite al paciente cancelar su turno
    const response = await axios.post(`${API_BASE_URL}/api/appointments/public/${token}/cancel`);
    return response.data;
  },

  acceptAppointment: async (appointmentId) => {
    const response = await apiClient.patch(`/api/appointments/${appointmentId}/accept`);
    return response.data;
  },

  rejectAppointment: async (appointmentId, reason = '') => {
    const response = await apiClient.patch(`/api/appointments/${appointmentId}/reject`, {
      reason
    });
    return response.data;
  }
};

// Servicios de Patients (próximamente)
export const patientAPI = {
  getPatients: async () => {
    const response = await apiClient.get('/api/patients');
    return response.data;
  },

  createPatient: async (data) => {
    const response = await apiClient.post('/api/patients', data);
    return response.data;
  },

  getPatient: async (patientId) => {
    const response = await apiClient.get(`/api/patients/${patientId}`);
    return response.data;
  },

  updatePatient: async (patientId, data) => {
    const response = await apiClient.patch(`/api/patients/${patientId}`, data);
    return response.data;
  },

  deletePatient: async (patientId) => {
    const response = await apiClient.delete(`/api/patients/${patientId}`);
    return response.data;
  }
};

// Servicios de Historial de Pacientes (Registros)
export const patientRecordAPI = {
  getFile: async id => (await apiClient.get(`/api/patient-records/file/${id}`, { responseType: 'blob' })).data,
  getRecords: async (patientId) => {
    const response = await apiClient.get(`/api/patient-records/${patientId}`);
    return response.data;
  },

  createRecord: async (patientId, formData) => {
    // Usamos axios directamente para poder enviar FormData (archivos)
    const token = localStorage.getItem('token');
    const response = await axios.post(`${API_BASE_URL}/api/patient-records/${patientId}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        'Authorization': `Bearer ${token}`,
        'ngrok-skip-browser-warning': 'true'
      }
    });
    return response.data;
  },

  deleteRecord: async (recordId) => {
    const response = await apiClient.delete(`/api/patient-records/${recordId}`);
    return response.data;
  }
};

// Servicios de Servicios (del médico)
export const serviceAPI = {
  getMyServices: async () => {
    const response = await apiClient.get('/api/services/doctor/me');
    return response.data;
  }
};

// Google Calendar Integration
export const queueAPI = {
  getDoctorQueueStatus: async () => {
    const response = await apiClient.get('/api/queue/status');
    return response.data;
  },

  registerPatientInQueue: async (data) => {
    const response = await apiClient.post('/api/queue/register', data);
    return response.data;
  },

  callNextPatient: async () => {
    const response = await apiClient.post('/api/queue/call-next');
    return response.data;
  },

  completePatient: async (queueId) => {
    const response = await apiClient.post('/api/queue/complete', { queueId });
    return response.data;
  },

  skipPatient: async (queueId, action = 'skipped') => {
    const response = await apiClient.post('/api/queue/skip', { queueId, action });
    return response.data;
  },

  toggleSelfRegistration: async (allow_self_queue) => {
    const response = await apiClient.patch('/api/queue/toggle-self-registration', { allow_self_queue });
    return response.data;
  },

  // Métodos públicos para paciente
  getPatientTrackingStatus: async (token) => {
    const response = await axios.get(`${API_BASE_URL}/api/queue/public/track/${token}`, {
      headers: { 'ngrok-skip-browser-warning': 'true' }
    });
    return response.data;
  },

  selfRegisterInQueue: async (doctorId, data) => {
    const response = await axios.post(`${API_BASE_URL}/api/queue/public/self-register/${doctorId}`, data, {
      headers: { 'ngrok-skip-browser-warning': 'true' }
    });
    return response.data;
  },

  getPublicBoard: async (doctorId) => {
    const response = await axios.get(`${API_BASE_URL}/api/queue/public/board/${doctorId}`, {
      headers: { 'ngrok-skip-browser-warning': 'true' }
    });
    return response.data;
  }
};

// Google Calendar Integration
export const googleAPI = {
  getStatus: async () => {
    const response = await apiClient.get('/api/google/status');
    return response.data;
  },

  connect: (token) => {
    window.location.href = `${API_BASE_URL}/api/google/auth?token=${token}`;
  },

  disconnect: async () => {
    const response = await apiClient.post('/api/google/disconnect');
    return response.data;
  }
};

// Servicios de Obras Sociales
export const insuranceAPI = {
  getInsurances: async () => {
    const response = await apiClient.get('/api/insurance');
    return response.data;
  },

  createInsurance: async (data) => {
    const response = await apiClient.post('/api/insurance', data);
    return response.data;
  },

  updateInsurance: async (id, data) => {
    const response = await apiClient.patch(`/api/insurance/${id}`, data);
    return response.data;
  },

  deleteInsurance: async (id) => {
    const response = await apiClient.delete(`/api/insurance/${id}`);
    return response.data;
  },

  getPatientInsurances: async (patientId) => {
    const response = await apiClient.get(`/api/insurance/patient/${patientId}`);
    return response.data;
  },

  setPatientInsurances: async (patientId, insuranceIds) => {
    const response = await apiClient.put(`/api/insurance/patient/${patientId}`, {
      insuranceIds
    });
    return response.data;
  },

  getPublicInsurances: async (doctorId) => {
    // Usamos axios directamente para rutas públicas sin token
    const response = await axios.get(`${API_BASE_URL}/api/insurance/public/doctor/${doctorId}`);
    return response.data;
  },
  getServiceCoverages: async (id) => {
    const response = await apiClient.get(`/api/insurance/${id}/coverages`);
    return response.data;
  },
  setServiceCoverage: async (id, data) => {
    const response = await apiClient.post(`/api/insurance/${id}/coverages`, data);
    return response.data;
  },
  exportInsurances: async () => {
    const response = await apiClient.get('/api/insurance/export', {
      responseType: 'blob'
    });
    return response.data;
  },
  importInsurances: async (formData) => {
    const response = await apiClient.post('/api/insurance/import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  },
  getPlans: async (insuranceId) => {
    const response = await apiClient.get(`/api/insurance/${insuranceId}/plans`);
    return response.data;
  },
  createPlan: async (insuranceId, data) => {
    const response = await apiClient.post(`/api/insurance/${insuranceId}/plans`, data);
    return response.data;
  },
  updatePlan: async (planId, data) => {
    const response = await apiClient.patch(`/api/insurance/plans/${planId}`, data);
    return response.data;
  },
  deletePlan: async (planId) => {
    const response = await apiClient.delete(`/api/insurance/plans/${planId}`);
    return response.data;
  },
  getTemplateCatalog: async () => {
    const response = await apiClient.get('/api/insurance/template-catalog');
    return response.data;
  },
  importFromCatalog: async (insuranceIds) => {
    const response = await apiClient.post('/api/insurance/template-catalog/import', { insuranceIds });
    return response.data;
  }
};

export default apiClient;

export const patientAccessAPI = {
 request: async data => (await axios.post(API_BASE_URL + '/api/patient-access/request', data)).data,
 verify: async data => (await axios.post(API_BASE_URL + '/api/patient-access/verify', data)).data
};
